import { LitElement, html } from 'lit';
import { property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { SDSIconLoader, type IconPromise } from './sds-icon-loader';
import { styles } from './sds-icon.styles';
import { SDS_SIZES } from '../types';

type IconSizes = Extract<SDS_SIZES, 'sm' | 'md' | 'lg'>;
export interface SDSIconProps {
	name?: string;
	size?: 's' | 'm' | IconSizes;
	color?: string;
	label?: string;
}

export class SDSIconComponent extends LitElement {
	static override styles = [styles];

	private static readonly iconLoader = new SDSIconLoader();

	static setLoadLocal(loadLocal: boolean): void {
		this.iconLoader.loadLocal = loadLocal;
	}

	static registerResolver(resolver: IconPromise): void {
		this.iconLoader.resolver = resolver;
	}

	@property({ type: String, reflect: true }) name: string = '';
	@property({ type: String, reflect: true }) size?: 's' | 'm' | IconSizes;
	@property({ type: String, reflect: true }) color: string = '';
	@property({ type: String, reflect: true }) label: string = '';

	@state() private svg: string = '';

	override render() {
		return html`
			<div class="sds-icon" role="${ifDefined(this.label ? 'img' : undefined)}" style="${ifDefined(this.color ? `color:${this.color}` : undefined)}" aria-label="${ifDefined(this.label)}">
				${this.name ? html`<slot aria-hidden="true">${unsafeSVG(this.svg)}</slot>` : html`<slot aria-hidden="true"></slot>`}
			</div>
		`;
	}

	/**
	 * Wraps the icon loader's callback in a promise. Even if the loader
	 * calls the callback synchronously, awaiting the promise defers the
	 * property update to the next microtask.
	 */
	private async fetchIcon() {
		if (!this.name) {
			this.svg = '';
			return;
		}
		const svg = await new Promise<string>((resolve) => {
			SDSIconComponent.iconLoader.resolve(this.name, resolve);
		});
		this.svg = svg;
	}

	override updated(changedProperties: Map<string | number | symbol, unknown>): void {
		super.updated(changedProperties);
		if (changedProperties.has('name') && this.name) {
			void this.fetchIcon();
		}
	}
}
